// src/components/admin/DealerOffersModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import useDealerBidding from '../../hooks/useDealerBidding';

const fmt = (val) => val ? `₹${Number(val).toLocaleString('en-IN')}` : '—';

const timeAgo = (dateStr) => {
  if (!dateStr) return '—';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};

const statusColor = (status) => {
  switch (status) {
    case 'accepted': return { bg:'#28a74520', color:'#28a745', border:'#28a74540' };
    case 'rejected': return { bg:'#dc354520', color:'#dc3545', border:'#dc354540' };
    default:         return { bg:'#FFD60A20', color:'#856404', border:'#FFD60A40' };
  }
};

/** Format a Date to "h:mm AM/PM" */
const fmtTime = (date) => {
  if (!date) return '—';
  return date.toLocaleTimeString('en-IN', { hour:'numeric', minute:'2-digit', hour12:true });
};

/** True when deal is finalised */
const isBiddingClosed = (listing) =>
  listing?.dealer_status  === 'deal_finalized' ||
  listing?.bidding_status === 'closed'         ||
  listing?.deal_closed_at != null              ||
  (listing?.bidding_end_at && new Date(listing.bidding_end_at) < new Date());

/**
 * Bid Window range for the header / price bar.
 * Start = bidding_start_at
 * End   = deal_closed_at (exact acceptance moment) OR bidding_end_at as fallback
 */
const getBidRange = (listing) => {
  const start  = listing?.bidding_start_at ? new Date(listing.bidding_start_at) : null;
  const endRaw = listing?.deal_closed_at || listing?.bidding_end_at;
  const end    = endRaw ? new Date(endRaw) : null;
  if (!start || !end) return null;
  return `${fmtTime(start)} – ${fmtTime(end)}`;
};

/* ── Countdown — shows range when closed, ticks when active ─ */
const Countdown = ({ dealerListing }) => {
  const [remaining, setRemaining] = useState('');
  const closed = isBiddingClosed(dealerListing);
  const endAt  = dealerListing?.bidding_end_at;

  useEffect(() => {
    if (closed || !endAt) return;
    const tick = () => {
      const ms = new Date(endAt) - Date.now();
      if (ms <= 0) { setRemaining('Ended'); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setRemaining(h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endAt, closed]);

  // Closed → static time range (start → actual close time)
  if (closed) {
    const range = getBidRange(dealerListing) || '—';
    return (
      <span style={{
        fontFamily:'monospace', fontWeight:800, fontSize:13,
        color:'#6c757d', background:'#6c757d15',
        border:'1px solid #6c757d40', borderRadius:6,
        padding:'3px 10px', letterSpacing:0.5,
      }}>
        ⏹ {range}
      </span>
    );
  }

  const isUrgent = endAt && (new Date(endAt) - Date.now()) < 3600000;
  return (
    <span style={{
      fontFamily:'monospace', fontWeight:800, fontSize:15,
      color:      isUrgent ? '#dc3545' : '#28a745',
      background: isUrgent ? '#dc354510' : '#28a74510',
      border:     `1px solid ${isUrgent ? '#dc354540' : '#28a74540'}`,
      borderRadius:6, padding:'3px 10px', letterSpacing:1,
    }}>
      ⏱ {remaining}
    </span>
  );
};

const LiveDot = ({ connected }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12 }}>
    <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:connected?'#28a745':'#dc3545', boxShadow:connected?'0 0 0 3px #28a74530':'none', animation:connected?'pulse 1.5s infinite':'none' }}/>
    <span style={{ color:connected?'#28a745':'#999', fontWeight:600 }}>{connected?'LIVE':'Connecting…'}</span>
  </span>
);

const BidFlash = ({ show }) => show ? (
  <span style={{ background:'#FFD60A', color:'#003566', fontSize:11, fontWeight:800, padding:'2px 8px', borderRadius:4, animation:'bidFlash 0.6s ease-out', letterSpacing:0.5 }}>NEW BID!</span>
) : null;

/* ═══════════════════════════════════════════════════════════
   DealerOffersModal
   ═══════════════════════════════════════════════════════════ */
const DealerOffersModal = ({ dealerListing: initialListing, onClose, adminApi }) => {
  const [dealerListing, setDealerListing] = useState(initialListing);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [actionId,  setActionId]  = useState(null);
  const [toast,     setToast]     = useState(null);
  const [connected, setConnected] = useState(false);
  const [newBidIds, setNewBidIds] = useState(new Set());
  const [liveStats, setLiveStats] = useState({ highest_offer:null, total_offers:0 });

  const closed = isBiddingClosed(dealerListing);

  const showToast = useCallback((msg, type='success') => {
    setToast({ msg, type }); setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchOffers = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res     = await adminApi.get(`/admin/dealer_listings/${dealerListing.id}/dealer_offers`);
      const payload = res.data?.success !== undefined
        ? res.data
        : Array.isArray(res.data)
        ? { success:true, total_offers:res.data.length, highest_offer:null, offers:res.data }
        : res.data;
      setData(payload);
      setLiveStats({ highest_offer:payload.highest_offer, total_offers:payload.total_offers??0 });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.statusText || 'Failed to load offers.');
    } finally { setLoading(false); }
  }, [dealerListing.id, adminApi]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  /* ActionCable */
  useDealerBidding(dealerListing.id, {
    onConnected:    () => setConnected(true),
    onDisconnected: () => setConnected(false),

    onNewBid: (payload) => {
      if (isBiddingClosed(dealerListing)) return;
      setLiveStats({ highest_offer:payload.highest_price, total_offers:payload.total_offers });
      setData(prev => {
        if (!prev) return prev;
        const exists     = prev.offers?.find(o => o.id === payload.offer_id);
        const newOffer   = { id:payload.offer_id, dealer_id:payload.dealer_id, dealer_name:`Dealer #${payload.dealer_id}`, offer_price:payload.price, status:'pending', created_at:new Date().toISOString(), _fresh:true };
        const updatedOffers = exists
          ? prev.offers.map(o => o.id===payload.offer_id ? {...o, offer_price:payload.price, _fresh:true} : o)
          : [...(prev.offers||[]), newOffer];
        return { ...prev, offers:updatedOffers, highest_offer:payload.highest_price, total_offers:payload.total_offers };
      });
      setNewBidIds(p => { const n=new Set(p); n.add(payload.offer_id); return n; });
      setTimeout(() => setNewBidIds(p => { const n=new Set(p); n.delete(payload.offer_id); return n; }), 3000);
      showToast(`New bid received: ${fmt(payload.price)}`);
    },

    onBidResult: (payload) => {
      // Admin accepted — record the exact close time from the broadcast payload
      setDealerListing(prev => ({
        ...prev,
        dealer_status:  'deal_finalized',
        bidding_status: 'closed',
        // Use the broadcast timestamp so the range reflects the real acceptance time
        deal_closed_at: payload.deal_closed_at || new Date().toISOString(),
      }));
      showToast('Deal has been finalized!');
      fetchOffers();
    },
  });

  /* Accept */
  const handleAccept = async (offerId) => {
    if (!window.confirm('Accept this offer? All other offers will be rejected.')) return;
    try {
      setActionId(offerId);
      const closedAt = new Date().toISOString();
      await adminApi.put(`/admin/dealer_offers/${offerId}/accept`);
      showToast('Offer accepted! Deal finalized.');
      // Stop timer immediately with current timestamp as end time
      setDealerListing(prev => ({
        ...prev,
        dealer_status:  'deal_finalized',
        bidding_status: 'closed',
        deal_closed_at: closedAt,
      }));
      await fetchOffers();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to accept offer.', 'error');
    } finally { setActionId(null); }
  };

  /* Reject */
  const handleReject = async (offerId) => {
    if (!window.confirm('Reject this offer?')) return;
    try {
      setActionId(offerId);
      await adminApi.put(`/admin/dealer_offers/${offerId}/reject`);
      showToast('Offer rejected.');
      await fetchOffers();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to reject offer.', 'error');
    } finally { setActionId(null); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
      <style>{`
        @keyframes spin     { to { transform: rotate(360deg); } }
        @keyframes pulse    { 0%,100%{box-shadow:0 0 0 3px #28a74530}50%{box-shadow:0 0 0 6px #28a74515} }
        @keyframes bidFlash { 0%{opacity:0;transform:scale(0.7)} 60%{opacity:1;transform:scale(1.1)} 100%{transform:scale(1)} }
        @keyframes rowSlide { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {toast && (
        <div style={{ position:'fixed', top:24, right:24, zIndex:2000, background:toast.type==='success'?'#28a745':'#dc3545', color:'#fff', padding:'13px 22px', borderRadius:10, fontWeight:700, fontSize:14, boxShadow:'0 4px 20px rgba(0,0,0,0.25)' }}>
          {toast.type==='success'?'✅ ':'❌ '}{toast.msg}
        </div>
      )}

      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:780, maxHeight:'92vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.35)', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#003566,#001d3d)', borderBottom:'3px solid #FFD60A' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <span style={{ background:'rgba(255,214,10,0.15)', borderRadius:8, padding:'6px 10px', border:'1px solid #FFD60A', fontSize:20 }}>🏷️</span>
            <div>
              <h2 style={{ margin:0, color:'#FFD60A', fontSize:18, fontWeight:800 }}>
                Dealer Offers — Listing #{dealerListing.id}
              </h2>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginTop:4 }}>
                {/* Live dot only when active */}
                {!closed && <LiveDot connected={connected} />}
                {/* Countdown (active) or time range (closed) */}
                <Countdown dealerListing={dealerListing} />
                {closed && (
                  <span style={{ background:'#dc354520', color:'#dc3545', border:'1px solid #dc354540', borderRadius:6, padding:'2px 10px', fontSize:12, fontWeight:800 }}>
                    🔒 Deal Closed
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, color:'#fff', width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:18, fontWeight:700 }}>×</button>
        </div>

        {/* Stats strip */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', background:'linear-gradient(90deg,#003566,#001d3d)', borderBottom:'1px solid #FFD60A30' }}>
          {[
            { label:'TOTAL BIDS',     value:liveStats.total_offers,              icon:'📋', gold:false },
            { label:'HIGHEST OFFER',  value:fmt(liveStats.highest_offer),         icon:'🏆', gold:true  },
            { label:'BIDDING STATUS', value:closed?'closed':(dealerListing.bidding_status||'active').replace(/_/g,' '), icon:'📡', gold:false },
          ].map(s => (
            <div key={s.label} style={{ padding:'14px 18px', borderRight:'1px solid rgba(255,214,10,0.15)', textAlign:'center' }}>
              <p style={{ fontSize:20, margin:'0 0 4px' }}>{s.icon}</p>
              <p style={{ fontSize:18, fontWeight:800, margin:'0 0 2px', color:s.gold?'#FFD60A':'#fff', textTransform:'capitalize' }}>{s.value}</p>
              <p style={{ fontSize:10, fontWeight:700, margin:0, color:'rgba(255,214,10,0.6)', textTransform:'uppercase', letterSpacing:0.8 }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:24 }}>
          {/* Price info bar */}
          <div style={{ background:'#f8f9fa', borderRadius:10, padding:'12px 18px', border:'1px solid #e0e0e0', marginBottom:20, display:'flex', gap:24, flexWrap:'wrap', alignItems:'center' }}>
            <div>
              <p style={{ fontSize:10, color:'#999', margin:'0 0 2px', fontWeight:700, textTransform:'uppercase' }}>Price Range</p>
              <p style={{ margin:0, fontWeight:700, color:'#003566', fontSize:13 }}>{fmt(dealerListing.price_range_min)} – {fmt(dealerListing.price_range_max)}</p>
            </div>
            <div>
              <p style={{ fontSize:10, color:'#999', margin:'0 0 2px', fontWeight:700, textTransform:'uppercase' }}>Expected</p>
              <p style={{ margin:0, fontWeight:800, color:'#856404', fontSize:13 }}>{fmt(dealerListing.expected_price)}</p>
            </div>
            {/* Bid window — always visible, uses deal_closed_at when closed */}
            {dealerListing.bidding_start_at && (
              <div>
                <p style={{ fontSize:10, color:'#999', margin:'0 0 2px', fontWeight:700, textTransform:'uppercase' }}>Bid Window</p>
                <p style={{ margin:0, fontWeight:700, color:'#444', fontSize:13, fontFamily:'monospace' }}>
                  {getBidRange(dealerListing) || '—'}
                </p>
              </div>
            )}
            <div style={{ marginLeft:'auto' }}>
              <p style={{ fontSize:10, color:'#999', margin:'0 0 2px', fontWeight:700, textTransform:'uppercase' }}>Real-time</p>
              {closed ? <span style={{ fontSize:12, color:'#dc3545', fontWeight:700 }}>🔒 Closed</span> : <LiveDot connected={connected} />}
            </div>
          </div>

          {loading && (
            <div style={{ textAlign:'center', padding:'48px 0' }}>
              <div style={{ width:40, height:40, border:'4px solid #e0e0e0', borderTopColor:'#003566', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }}/>
              <p style={{ color:'#666', margin:0 }}>Loading offers...</p>
            </div>
          )}

          {error && !loading && (
            <div style={{ background:'#dc354510', border:'2px solid #dc3545', borderRadius:12, padding:'20px', color:'#dc3545', textAlign:'center' }}>
              <p style={{ fontSize:28, margin:'0 0 8px' }}>⚠️</p>
              <p style={{ margin:'0 0 4px', fontWeight:700 }}>Failed to load offers</p>
              <p style={{ margin:0, fontSize:13 }}>{error}</p>
              <button onClick={fetchOffers} style={{ marginTop:14, padding:'8px 20px', background:'#dc3545', border:'none', borderRadius:8, color:'#fff', fontWeight:700, cursor:'pointer' }}>Try Again</button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {(!data.offers || data.offers.length === 0) ? (
                <div style={{ textAlign:'center', padding:'48px 20px', background:'#f5f5f5', borderRadius:12, border:'2px dashed #e0e0e0' }}>
                  <p style={{ fontSize:40, margin:'0 0 12px' }}>📭</p>
                  <p style={{ color:'#333', fontSize:16, fontWeight:700, margin:'0 0 6px' }}>No offers received yet</p>
                  <p style={{ color:'#999', fontSize:13, margin:0 }}>Dealers will submit offers live. You'll see them appear here instantly.</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                    <p style={{ margin:0, fontSize:13, color:'#666', fontWeight:600 }}>{data.offers.length} offer{data.offers.length!==1?'s':''} — sorted by price</p>
                    <span style={{ fontSize:12, color:closed?'#dc3545':'#003566', fontWeight:700 }}>{closed?'🔒 Bidding closed':'🔴 Live updates active'}</span>
                  </div>

                  {[...data.offers].sort((a,b)=>Number(b.offer_price)-Number(a.offer_price)).map((offer, idx) => {
                    const sc        = statusColor(offer.status || 'pending');
                    const isTop     = idx === 0;
                    const acting    = actionId === offer.id;
                    const isPending = !offer.status || offer.status === 'pending';
                    const isNew     = newBidIds.has(offer.id);

                    return (
                      <div key={offer.id} style={{
                        background: isNew ? 'linear-gradient(135deg,rgba(255,214,10,0.12),rgba(255,193,7,0.06))' : isTop ? 'linear-gradient(135deg,rgba(255,214,10,0.06),rgba(255,193,7,0.02))' : offer.status==='accepted' ? 'linear-gradient(135deg,rgba(40,167,69,0.06),rgba(40,167,69,0.02))' : '#fff',
                        border: isNew ? '2px solid #FFD60A' : isTop ? '2px solid #FFD60A' : offer.status==='accepted' ? '2px solid #28a745' : '2px solid #e0e0e0',
                        borderRadius:12, padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
                        opacity: offer.status==='rejected' ? 0.55 : 1, transition:'all 0.3s ease',
                        animation: offer._fresh ? 'rowSlide 0.4s ease' : 'none',
                      }}>
                        <div style={{ display:'flex', alignItems:'center', gap:14, flex:1, minWidth:0 }}>
                          <div style={{ width:44, height:44, borderRadius:'50%', flexShrink:0, background:isTop?'linear-gradient(135deg,#FFD60A,#FFC107)':'linear-gradient(135deg,#003566,#001d3d)', color:isTop?'#003566':'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:15, boxShadow:isTop?'0 0 0 3px #FFD60A40':'none' }}>
                            #{idx+1}
                          </div>
                          <div style={{ minWidth:0 }}>
                            <p style={{ margin:'0 0 2px', fontWeight:700, fontSize:14, color:'#0a0a0a', display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                              {offer.dealer_name || `Dealer #${offer.dealer_id}`}
                              {isTop && isPending && <span style={{ background:'#FFD60A', color:'#003566', fontSize:9, fontWeight:800, padding:'2px 6px', borderRadius:4, letterSpacing:0.5 }}>TOP</span>}
                              <BidFlash show={isNew} />
                            </p>
                            <p style={{ margin:0, fontSize:11, color:'#999' }}>Offer #{offer.id} • {timeAgo(offer.created_at)}</p>
                          </div>
                        </div>

                        <div style={{ textAlign:'center', flexShrink:0, minWidth:130 }}>
                          <p style={{ fontSize:22, fontWeight:800, margin:0, color:offer.status==='accepted'?'#28a745':isTop?'#003566':'#444', transition:'color 0.3s' }}>
                            {fmt(offer.offer_price)}
                          </p>
                        </div>

                        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                          <span style={{ padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700, textTransform:'capitalize', background:sc.bg, color:sc.color, border:`1px solid ${sc.border}` }}>
                            {offer.status || 'pending'}
                          </span>
                          {/* Accept/Reject only when still open */}
                          {isPending && !closed && (
                            <>
                              <button onClick={()=>handleAccept(offer.id)} disabled={acting} style={{ padding:'7px 14px', border:'none', borderRadius:7, background:acting?'#ccc':'#28a745', color:'#fff', fontWeight:700, fontSize:12, cursor:acting?'not-allowed':'pointer', whiteSpace:'nowrap' }}>
                                {acting?'⏳':'✓ Accept'}
                              </button>
                              <button onClick={()=>handleReject(offer.id)} disabled={acting} style={{ padding:'7px 14px', border:'none', borderRadius:7, background:acting?'#ccc':'#dc3545', color:'#fff', fontWeight:700, fontSize:12, cursor:acting?'not-allowed':'pointer', whiteSpace:'nowrap' }}>
                                {acting?'⏳':'✕ Reject'}
                              </button>
                            </>
                          )}
                          {offer.status==='accepted' && <span style={{ fontSize:22 }}>🏆</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 24px', borderTop:'2px solid #e0e0e0', background:'#f8f9fa' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <button onClick={fetchOffers} disabled={loading} style={{ padding:'10px 20px', background:'#fff', border:'2px solid #003566', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer', color:'#003566', display:'flex', alignItems:'center', gap:6 }}>
              🔄 Refresh
            </button>
            {!closed && <LiveDot connected={connected} />}
          </div>
          <button onClick={onClose} style={{ padding:'10px 24px', background:'linear-gradient(135deg,#003566,#001d3d)', border:'2px solid #FFD60A', borderRadius:8, color:'#FFD60A', fontWeight:700, fontSize:13, cursor:'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealerOffersModal;